import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    AlertTriangle,
    Bot,
    CalendarPlus,
    Check,
    CheckCircle2,
    Clock,
    Copy,
    Download,
    ExternalLink,
    FileText,
    Info,
    Paperclip,
    ThumbsDown,
    ThumbsUp,
    Upload,
    User,
    Volume2,
    VolumeX,
    X,
} from "lucide-react";

import { cn } from "../../utils/tailwindUtils";
import { Button } from "../ui/button";
import apiClient from "../../lib/apiClient";
import ToastMsg from "../common/ToastMsg";
import { API_URL } from "../../constants/api";
import CopilotDataCards from "./CopilotDataCards";
import CopilotMarkdown from "./CopilotMarkdown";
import SalaryVisualizerCard from "./SalaryVisualizerCard";
import ManagerDigestCard from "./ManagerDigestCard";
import { getDomainMeta } from "./constants";

const LeaveActionDraftCard = ({ draft }) => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(draft?.status === 'submitted');
    const [resultMessage, setResultMessage] = useState('');
    const [closed, setClosed] = useState(false);

    if (!draft || closed) return null;

    const availableTypes = draft?.available_leave_types || [
        { id: draft?.leave_type_id, leave_name: draft?.leave_type_name || 'Casual Leave' }
    ];

    const handleConfirmSubmit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                action_type: 'apply_leave',
                leave_request_type: draft.leave_request_type || 'leave',
                leave_type_id: draft.leave_type_id || availableTypes[0]?.id,
                period: 'full_day',
                from_date: draft.from_date,
                to_date: draft.to_date,
                reason: draft.reason || 'Applied via AI Copilot',
            };
            const resData = await apiClient.post('ai/execute-action/', payload);
            if (resData?.status === 'success') {
                setSubmitted(true);
                setResultMessage(resData.message || 'Leave request submitted successfully!');
            } else {
                ToastMsg('error', resData?.message || 'Failed to submit leave request');
            }
        } catch (e) {
            console.error('Error submitting action:', e);
            ToastMsg('error', e?.response?.data?.message || 'Something went wrong submitting your request.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-50/50 p-3.5 dark:bg-emerald-950/20 shadow-sm animate-fadeIn text-xs">
            <div className="flex items-center justify-between border-b border-emerald-500/20 pb-2.5 mb-2.5">
                <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300 font-semibold text-xs">
                    <CalendarPlus className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    <span>One-Click Action: {draft.leave_type_name}</span>
                </div>
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-200/70 dark:bg-emerald-900/60 text-emerald-900 dark:text-emerald-200">
                    ⚡ Auto-Selected from Balance
                </span>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <div className="bg-background/90 p-2.5 rounded-lg border border-emerald-500/20">
                    <span className="text-[10px] text-muted-foreground block uppercase font-medium">Leave Type</span>
                    <span className="font-semibold text-foreground">{draft.leave_type_name}</span>
                    {draft.available_days !== undefined && (
                        <span className="text-[10px] text-emerald-600 dark:text-emerald-400 block font-medium mt-0.5">
                            ({draft.available_days} Days Available)
                        </span>
                    )}
                </div>
                <div className="bg-background/90 p-2.5 rounded-lg border border-emerald-500/20">
                    <span className="text-[10px] text-muted-foreground block uppercase font-medium">Duration</span>
                    <span className="font-semibold text-foreground">{draft.days} Day(s)</span>
                    <span className="text-[10px] text-muted-foreground block mt-0.5">{draft.from_date}</span>
                </div>
                <div className="col-span-2 bg-background/90 p-2.5 rounded-lg border border-emerald-500/20">
                    <span className="text-[10px] text-muted-foreground block uppercase font-medium">Reason</span>
                    <span className="font-medium text-foreground">{draft.reason}</span>
                </div>
            </div>

            {submitted ? (
                <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-300 font-medium bg-emerald-100 dark:bg-emerald-900/40 p-2.5 rounded-lg">
                        <CheckCircle2 className="h-4.5 w-4.5 shrink-0 text-emerald-600" />
                        <span>{resultMessage || 'Leave application successfully logged into HRMS!'}</span>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            onClick={() => navigate('/employee/leaverequest')}
                            className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 text-white hover:bg-emerald-700 text-xs font-semibold py-1.5 rounded-lg cursor-pointer"
                        >
                            <ExternalLink className="h-3.5 w-3.5" />
                            View Leave Requests
                        </Button>
                    </div>
                </div>
            ) : (
                <Button
                    size="sm"
                    disabled={submitting}
                    onClick={handleConfirmSubmit}
                    className="w-full flex items-center justify-center gap-2 bg-[#00B49F] hover:bg-[#009b89] text-white text-xs font-semibold py-2.5 rounded-lg shadow-sm transition-all cursor-pointer"
                >
                    <CheckCircle2 className="h-4 w-4" />
                    {submitting ? 'Submitting to HRMS...' : '⚡ Confirm & Submit Leave Request'}
                </Button>
            )}
        </div>
    );
};

const ApplyLeaveShortcutCard = () => {
    const navigate = useNavigate();
    return (
        <div className="mt-3.5 rounded-xl border border-sidebar/20 bg-sidebar/5 p-3.5 shadow-sm animate-fadeIn">
            <div className="flex items-center justify-between border-b border-sidebar/15 pb-2 mb-2">
                <div className="flex items-center gap-2 text-sidebar font-semibold text-xs">
                    <CalendarPlus className="h-4 w-4 text-sidebar" />
                    <span>Apply / Request Leave</span>
                </div>
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-sidebar/10 text-sidebar font-semibold">
                    Shortcut
                </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
                Click below to open the Leave Request form directly in HRMS.
            </p>
            <Button
                size="sm"
                onClick={() => navigate("/employee/leaverequest")}
                className="w-full flex items-center justify-center gap-2 bg-sidebar text-white hover:bg-sidebar-primary text-xs font-semibold py-2 rounded-lg shadow-md transition-all cursor-pointer"
            >
                <ExternalLink className="h-3.5 w-3.5" />
                Go to Leave Request Form
            </Button>
        </div>
    );
};

const WeekendWeekoffConfirmationCard = ({ queryText = "" }) => {
    const navigate = useNavigate();
    const [confirmed, setConfirmed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [satOff, setSatOff] = useState(true);
    const [sunOff, setSunOff] = useState(true);
    const [policyName, setPolicyName] = useState("Standard Weekend Off (Sat & Sun)");

    const handleApplyWeekoff = async () => {
        setSaving(true);
        try {
            const satStatus = satOff ? 'fullday' : 'working_day';
            const sunStatus = sunOff ? 'fullday' : 'working_day';
            const weekoffList = [1, 2, 3, 4, 5].map(wNum => ({
                week: wNum,
                monday: 'working_day',
                tuesday: 'working_day',
                wednesday: 'working_day',
                thursday: 'working_day',
                friday: 'working_day',
                saturday: satStatus,
                sunday: sunStatus
            }));

            const payload = {
                weekoff_name: policyName.trim() || "Standard Weekend Off",
                weekoff_rules: "Saturday and Sunday recurring weekend off",
                notes: "Configured via HRZyra AI Copilot",
                is_active: true,
                is_draft: false,
                weekoff_list: weekoffList
            };
            const resData = await apiClient.post("configuration/weekoff/", payload);

            if (resData?.status === "success" || resData?.message?.toLowerCase().includes("success") || resData?.id) {
                setConfirmed(true);
                ToastMsg("success", `Week Off policy "${policyName}" applied successfully! HRMS Calendar & Setup updated.`);
            } else {
                ToastMsg("error", resData?.message || "Failed to save Week Off policy.");
            }
        } catch (e) {
            console.error("Error applying week off:", e);
            const errMsg = e?.response?.data?.message || e?.response?.data?.errors?.[0] || e?.message || "Could not apply Week Off policy";
            if (errMsg.toLowerCase().includes("already exist")) {
                ToastMsg("info", "A Week Off policy with this name or for this department already exists.");
                setConfirmed(true);
            } else {
                ToastMsg("error", errMsg);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-3.5 rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-3.5 shadow-sm animate-fadeIn">
            <div className="flex items-center justify-between border-b border-indigo-500/20 pb-2 mb-2">
                <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300 font-semibold text-xs">
                    <CalendarPlus className="h-4 w-4 text-indigo-600" />
                    <span>Apply Recurring Week Off Policy</span>
                </div>
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-800 dark:text-indigo-200 font-semibold">
                    {confirmed ? 'Applied in Calendar' : 'Confirmation Required'}
                </span>
            </div>

            <p className="text-xs text-muted-foreground mb-3">
                Do you want to confirm applying <strong>"{policyName}"</strong> for all weeks in the HRMS Calendar and Week Off Setup?
            </p>

            {!confirmed && (
                <div className="space-y-2 mb-3 bg-indigo-500/10 p-2.5 rounded-lg border border-indigo-500/20">
                    <div className="flex items-center gap-2">
                        <label className="text-[11px] font-medium text-indigo-900 dark:text-indigo-100 whitespace-nowrap">Policy Name:</label>
                        <input
                            type="text"
                            value={policyName}
                            onChange={(e) => setPolicyName(e.target.value)}
                            className="h-7 px-2.5 text-xs border border-indigo-300 rounded bg-white dark:bg-gray-800 text-foreground font-medium flex-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                    </div>
                    <div className="flex items-center gap-4 text-xs pt-1">
                        <span className="text-[11px] font-medium text-indigo-900 dark:text-indigo-100">Recurring Days:</span>
                        <label className="flex items-center gap-1.5 cursor-pointer font-medium text-indigo-950 dark:text-indigo-200">
                            <input
                                type="checkbox"
                                checked={satOff}
                                onChange={(e) => setSatOff(e.target.checked)}
                                className="rounded text-indigo-600 focus:ring-indigo-500"
                            />
                            Saturday
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer font-medium text-indigo-950 dark:text-indigo-200">
                            <input
                                type="checkbox"
                                checked={sunOff}
                                onChange={(e) => setSunOff(e.target.checked)}
                                className="rounded text-indigo-600 focus:ring-indigo-500"
                            />
                            Sunday
                        </label>
                    </div>
                </div>
            )}

            {confirmed ? (
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300 font-medium bg-emerald-100 dark:bg-emerald-900/40 p-2.5 rounded-lg border border-emerald-500/20">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                        <span>Successfully applied "{policyName}" policy in Calendar & Week Off Setup!</span>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            onClick={() => navigate("/calendar")}
                            className="flex-1 flex items-center justify-center gap-2 bg-sidebar text-white hover:bg-sidebar-primary text-xs font-semibold py-2 rounded-lg shadow-md transition-all cursor-pointer"
                        >
                            <CalendarPlus className="h-3.5 w-3.5" />
                            View Calendar
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate("/configuration/weekoff")}
                            className="flex-1 flex items-center justify-center gap-2 border-indigo-600/40 text-indigo-800 dark:text-indigo-200 hover:bg-indigo-500/10 text-xs font-medium py-2 rounded-lg transition-all cursor-pointer"
                        >
                            <ExternalLink className="h-3.5 w-3.5" />
                            Week Off Setup
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex gap-2">
                    <Button
                        size="sm"
                        disabled={saving}
                        onClick={handleApplyWeekoff}
                        className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold py-2 rounded-lg shadow-md transition-all cursor-pointer"
                    >
                        {saving ? (
                            <div className="flex items-center gap-2">
                                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                <span>Applying Policy...</span>
                            </div>
                        ) : (
                            <>
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                <span>⚡ Confirm & Apply Week Off Policy</span>
                            </>
                        )}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate("/configuration/weekoff")}
                        className="flex items-center justify-center gap-1.5 border-indigo-500/30 text-indigo-800 dark:text-indigo-200 hover:bg-indigo-500/10 text-xs font-medium px-3 py-2 rounded-lg transition-all cursor-pointer"
                    >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Open Setup
                    </Button>
                </div>
            )}
        </div>
    );
};

const CalendarHolidayConfirmationCard = ({ queryText = "" }) => {
    const navigate = useNavigate();
    const [confirmed, setConfirmed] = useState(false);
    const [saving, setSaving] = useState(false);

    const parseQueryDate = (text) => {
        if (!text) return null;
        // Check DD/MM/YYYY or DD-MM-YYYY (e.g. 09/08/2026)
        const ddmmyyyy = text.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
        if (ddmmyyyy) {
            let [_, day, month, year] = ddmmyyyy;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        // Check YYYY-MM-DD (e.g. 2026-08-09)
        const yyyymmdd = text.match(/(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
        if (yyyymmdd) {
            let [_, year, month, day] = yyyymmdd;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        return null;
    };

    const parseQueryName = (text) => {
        if (!text) return "Company Holiday";
        const lower = text.toLowerCase();
        if (lower.includes("sunday")) return "Sunday Holiday";
        
        const matchAs = text.match(/as\s+(?:a\s+)?([a-zA-Z0-9\s]+?)(?:\s+holiday)?$/i);
        if (matchAs && matchAs[1].trim()) {
            const cleanName = matchAs[1].trim();
            if (cleanName.toLowerCase() !== "company" && cleanName.toLowerCase() !== "holiday") {
                return cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
            }
        }
        return "Company Holiday";
    };

    // Compute initial date from queryText or default to tomorrow YYYY-MM-DD
    const [targetDate, setTargetDate] = useState(() => {
        const parsed = parseQueryDate(queryText);
        if (parsed) return parsed;
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split("T")[0];
    });

    const [holidayName, setHolidayName] = useState(() => parseQueryName(queryText));
    const [userEditedName, setUserEditedName] = useState(false);

    React.useEffect(() => {
        const parsedDate = parseQueryDate(queryText);
        if (parsedDate) {
            setTargetDate(parsedDate);
            setConfirmed(false);
        }
        if (!userEditedName) {
            setHolidayName(parseQueryName(queryText));
        }
    }, [queryText, userEditedName]);

    const formattedDate = new Date(targetDate + "T00:00:00").toLocaleDateString("en-US", {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });

    const handleConfirmSubmit = async () => {
        setSaving(true);
        try {
            const finalName = holidayName.trim() || `Company Holiday (${targetDate})`;
            const payload = {
                holiday_name: finalName,
                holiday_type: "others",
                date: targetDate,
                notes: "Marked via HRZyra AI Copilot",
                is_active: true,
                is_draft: false
            };
            const resData = await apiClient.post("configuration/holiday/", payload);
            if (resData?.status === "success" || resData?.message?.toLowerCase().includes("success") || resData?.id) {
                setConfirmed(true);
                ToastMsg("success", `Holiday "${finalName}" successfully saved for ${formattedDate} in Calendar & Holiday Setup!`);
            } else {
                ToastMsg("error", resData?.message || "Failed to create holiday.");
            }
        } catch (e) {
            console.error("Error creating holiday:", e);
            const errMsg = e?.response?.data?.message || e?.response?.data?.errors?.[0] || e?.message || "Holiday could not be added";
            if (errMsg.toLowerCase().includes("already exist") || errMsg.toLowerCase().includes("already marked")) {
                ToastMsg("info", `Holiday date ${targetDate} or name already exists in HRMS.`);
                setConfirmed(true);
            } else {
                ToastMsg("error", errMsg);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-3.5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3.5 shadow-sm animate-fadeIn">
            <div className="flex items-center justify-between border-b border-amber-500/20 pb-2 mb-2">
                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 font-semibold text-xs">
                    <CalendarPlus className="h-4 w-4 text-amber-600" />
                    <span>Company Calendar Action</span>
                </div>
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-800 dark:text-amber-200 font-semibold">
                    {confirmed ? 'Marked in Calendar' : 'Confirmation Required'}
                </span>
            </div>

            <p className="text-xs text-muted-foreground mb-2">
                Do you want to confirm marking <strong>{formattedDate}</strong> as <strong>"{holidayName || 'Company Holiday'}"</strong> in the HRMS Calendar and Holiday Setup?
            </p>

            {!confirmed && (
                <div className="flex flex-wrap items-center gap-3 mb-3 bg-amber-500/10 p-2.5 rounded-lg border border-amber-500/20">
                    <div className="flex items-center gap-2">
                        <label className="text-[11px] font-medium text-amber-900 dark:text-amber-100 whitespace-nowrap">Holiday Name:</label>
                        <input
                            type="text"
                            value={holidayName}
                            onChange={(e) => {
                                setHolidayName(e.target.value);
                                setUserEditedName(true);
                            }}
                            placeholder="e.g. Sunday Holiday"
                            className="h-7 px-2.5 text-xs border border-amber-300 rounded bg-white dark:bg-gray-800 text-foreground font-medium min-w-[140px] focus:outline-none focus:ring-1 focus:ring-amber-500"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-[11px] font-medium text-amber-900 dark:text-amber-100 whitespace-nowrap">Target Date:</label>
                        <input
                            type="date"
                            value={targetDate}
                            onChange={(e) => setTargetDate(e.target.value)}
                            className="h-7 px-2 text-xs border border-amber-300 rounded bg-white dark:bg-gray-800 text-foreground font-medium focus:outline-none focus:ring-1 focus:ring-amber-500"
                        />
                    </div>
                </div>
            )}

            {confirmed ? (
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300 font-medium bg-emerald-100 dark:bg-emerald-900/40 p-2.5 rounded-lg border border-emerald-500/20">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                        <span>Successfully marked holiday for {formattedDate} in Calendar & Holiday Setup!</span>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            onClick={() => navigate("/calendar")}
                            className="flex-1 flex items-center justify-center gap-2 bg-sidebar text-white hover:bg-sidebar-primary text-xs font-semibold py-2 rounded-lg shadow-md transition-all cursor-pointer"
                        >
                            <CalendarPlus className="h-3.5 w-3.5" />
                            View Calendar
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate("/configuration/holiday")}
                            className="flex-1 flex items-center justify-center gap-2 border-amber-600/40 text-amber-800 dark:text-amber-200 hover:bg-amber-500/10 text-xs font-medium py-2 rounded-lg transition-all cursor-pointer"
                        >
                            <ExternalLink className="h-3.5 w-3.5" />
                            Holiday Setup
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex gap-2">
                    <Button
                        size="sm"
                        disabled={saving}
                        onClick={handleConfirmSubmit}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold py-2 rounded-lg shadow-md transition-all cursor-pointer"
                    >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {saving ? "Saving to HRMS..." : "⚡ Confirm & Save Holiday"}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate("/configuration/holiday")}
                        className="flex items-center justify-center gap-1.5 border-amber-600/40 text-amber-800 dark:text-amber-200 hover:bg-amber-500/10 text-xs font-medium py-2 rounded-lg transition-all cursor-pointer"
                    >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Open Setup
                    </Button>
                </div>
            )}
        </div>
    );
};

const DownloadPayslipCard = ({ payslipData, queryText = "" }) => {
    const navigate = useNavigate();
    const [downloading, setDownloading] = useState(false);

    const payslipId = payslipData?.payslip_id || payslipData?.id || 172;
    const period = payslipData?.period || (payslipData?.payroll_month ? `${payslipData.payroll_month}/${payslipData.payroll_year}` : "August 2026");

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const token = sessionStorage.getItem("token") || localStorage.getItem("token") || localStorage.getItem("accessToken");
            const baseUrl = API_URL || (window.location.origin.includes('localhost') ? 'http://localhost:8000' : '');
            const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
            const downloadUrl = payslipId ? `${cleanBaseUrl}/payroll/payslip/print/?id=${payslipId}` : `${cleanBaseUrl}/payroll/payslip/print/`;

            const headers = {};
            if (token) {
                headers["Authorization"] = token.toLowerCase().startsWith("token ") || token.toLowerCase().startsWith("bearer ") ? token : `token ${token}`;
            }

            const response = await fetch(downloadUrl, {
                method: "GET",
                headers: headers,
            });

            if (!response.ok) {
                throw new Error(`Server status ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Payslip_${period.replace(/\s+/g, "_")}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            ToastMsg("success", "Payslip downloaded successfully!");
        } catch (err) {
            console.error("Payslip download error:", err);
            ToastMsg("error", "Failed to download payslip PDF. Please verify your session.");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="mt-3.5 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3.5 shadow-sm animate-fadeIn">
            <div className="flex items-center justify-between border-b border-emerald-500/20 pb-2 mb-2">
                <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 font-semibold text-xs">
                    <Download className="h-4 w-4 text-emerald-600" />
                    <span>Payslip Document Download</span>
                </div>
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-800 dark:text-emerald-200 font-semibold">
                    PDF Ready
                </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
                Your official salary slip for <strong className="font-semibold text-foreground">{period}</strong> is generated. Click below to download the PDF:
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
                <Button
                    size="sm"
                    disabled={downloading}
                    onClick={handleDownload}
                    className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold py-2 rounded-lg shadow-md transition-all cursor-pointer"
                >
                    <Download className="h-4 w-4" />
                    {downloading ? 'Downloading PDF...' : `📥 Download ${period} Payslip (PDF)`}
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate("/payroll/payslips")}
                    className="flex items-center justify-center gap-1.5 border-emerald-600/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/10 text-xs font-medium py-2 rounded-lg transition-all cursor-pointer"
                >
                    <FileText className="h-3.5 w-3.5" />
                    View All Payslips
                </Button>
            </div>
        </div>
    );
};

const NavigationActionCard = ({ navigation, queryText = "" }) => {
    const navigate = useNavigate();

    if (navigation?.exists === false || !navigation?.route || !navigation?.label || navigation?.label === "Requested Screen") {
        return null;
    }

    let target = navigation;

    return (
        <div className="mt-3 rounded-xl border border-primary/30 bg-primary/5 p-3.5 shadow-sm animate-fadeIn">
            <div className="flex items-center justify-between border-b border-primary/20 pb-2 mb-2">
                <div className="flex items-center gap-2 text-primary font-semibold text-xs">
                    <ExternalLink className="h-4 w-4 text-primary" />
                    <span>Quick Navigation Shortcut</span>
                </div>
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/20 text-primary font-semibold">
                    Interactive Route
                </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
                Click the button below to open <strong className="font-semibold text-foreground">{target.label}</strong> immediately:
            </p>
            <Button
                size="sm"
                onClick={() => navigate(target.route)}
                className="w-full flex items-center justify-center gap-2 bg-sidebar text-white hover:bg-sidebar-primary text-xs font-semibold py-2 rounded-lg shadow-md transition-all cursor-pointer"
            >
                <ExternalLink className="h-3.5 w-3.5" />
                🚀 Go to {target.label}
            </Button>
        </div>
    );
};

/**
 * One turn in the copilot thread.
 *
 * `compact` renders the widget variant (smaller type, 2-column metric tiles,
 * icon-only feedback); the default renders the full-page variant.
 */
const CopilotMessage = ({ message, compact = false, feedback, onRate }) => {
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);
    const [speaking, setSpeaking] = useState(false);
    const isUser = message.sender === "user";
    const domainMeta = getDomainMeta(message.domain);
    const DomainIcon = domainMeta.icon;

    const handleCopy = () => {
        // clipboard is unavailable on insecure origins — fail quietly, the
        // answer is still selectable.
        navigator.clipboard?.writeText(message.text || "").then(
            () => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1800);
            },
            () => { }
        );
    };

    const toggleSpeak = () => {
        if (!window.speechSynthesis) return;

        if (speaking) {
            window.speechSynthesis.cancel();
            setSpeaking(false);
        } else {
            window.speechSynthesis.cancel();
            const cleanText = (message.text || "").replace(/[*_#`~]/g, "");
            const utterance = new SpeechSynthesisUtterance(cleanText);
            utterance.rate = 1.0;
            utterance.onend = () => setSpeaking(false);
            utterance.onerror = () => setSpeaking(false);
            setSpeaking(true);
            window.speechSynthesis.speak(utterance);
        }
    };

    const avatar = (
        <div
            className={cn(
                "flex shrink-0 items-center justify-center rounded-lg",
                compact ? "h-7 w-7" : "h-9 w-9",
                isUser
                    ? "border border-border bg-card text-sidebar-primary"
                    : "bg-sidebar text-white shadow-sm"
            )}
        >
            {isUser ? (
                <User className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
            ) : (
                <Bot className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
            )}
        </div>
    );

    return (
        <div className={cn("flex flex-col", isUser ? "items-end" : "items-start")}>
            <div
                className={cn(
                    "flex max-w-full items-start gap-2",
                    compact ? "sm:max-w-[92%]" : "sm:max-w-[88%]",
                    isUser && "flex-row-reverse"
                )}
            >
                {avatar}

                <div
                    className={cn(
                        "min-w-0 rounded-xl px-3.5 py-2.5",
                        compact ? "text-xs" : "text-[13px]",
                        isUser
                            ? "rounded-br-sm bg-sidebar text-white"
                            : message.isError
                                ? "rounded-bl-sm border border-destructive/30 bg-destructive/10 text-foreground"
                                : "rounded-bl-sm border border-border bg-muted/50 text-foreground"
                    )}
                >
                    {/* User Attached Document Badge */}
                    {isUser && message.attachment && (
                        <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-md bg-white/20 px-2 py-1 text-xs font-medium">
                            <Paperclip className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate max-w-[200px]">{message.attachment.name}</span>
                        </div>
                    )}
                    {/* AI header: domain badge, source, copy */}
                    {!isUser && !message.isError && (
                        <div className="mb-2 flex items-center justify-between gap-2">
                            <span
                                className={cn(
                                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold",
                                    domainMeta.tint
                                )}
                            >
                                <DomainIcon className="h-3 w-3" />
                                {domainMeta.label}
                            </span>

                            <div className="flex items-center gap-2">
                                {/* Tells the user whether the wording came from the model
                                    or the deterministic HRMS fallback. */}
                                {message.isLlm === false && (
                                    <span
                                        className="text-[10px] text-muted-foreground"
                                        title="Answered directly from HRMS data (AI wording unavailable)"
                                    >
                                        HRMS data
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={toggleSpeak}
                                    title={speaking ? "Stop reading" : "Read aloud"}
                                    className="text-muted-foreground transition-colors hover:text-sidebar-primary"
                                >
                                    {speaking ? (
                                        <VolumeX className="h-3.5 w-3.5 text-red-500 animate-pulse" />
                                    ) : (
                                        <Volume2 className="h-3.5 w-3.5" />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCopy}
                                    title="Copy answer"
                                    className="text-muted-foreground transition-colors hover:text-sidebar-primary"
                                >
                                    {copied ? (
                                        <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                                    ) : (
                                        <Copy className="h-3.5 w-3.5" />
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {message.isError ? (
                        <p className="flex items-start gap-2 leading-relaxed">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                            <span>{message.text}</span>
                        </p>
                    ) : isUser ? (
                        <p className="whitespace-pre-wrap break-words leading-relaxed">
                            {message.text}
                        </p>
                    ) : (
                        <CopilotMarkdown content={message.text} />
                    )}

                    {/* An org-wide question answered with the caller's own scope. */}
                    {message.isRestricted && (
                        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-100 px-2 py-1.5 text-[11px] text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                            <Info className="mt-px h-3 w-3 shrink-0" />
                            Scoped to your own records — organisation-wide data needs HR access.
                        </p>
                    )}

                    {!isUser && !message.isError && (
                        <>
                            {message.data?.action_draft &&
                             message.data.action_draft.action_type === 'apply_leave' &&
                             !message.data?.is_cancellation &&
                             !(message.text && (
                                 message.text.toLowerCase().includes("cancel") ||
                                 message.text.toLowerCase().includes("withdraw") ||
                                 message.text.toLowerCase().includes("revoke")
                             )) && (
                                <LeaveActionDraftCard draft={message.data.action_draft} />
                            )}
                            {(message.domain === "navigation" || message.intent === "navigate") && message.data?.navigation?.route && (
                                <NavigationActionCard navigation={message.data.navigation} queryText={message.text} />
                            )}
                            {/* Structured data metric tiles */}
                            {(() => {
                                if (!message.data) return null;
                                const msgText = (message.text || "").toLowerCase();

                                // Exclude data tiles for holiday, calendar, navigation, draft actions, or non-metric requests
                                if (
                                    msgText.includes("holiday") ||
                                    msgText.includes("calendar") ||
                                    msgText.includes("mark ") ||
                                    msgText.includes("add ") ||
                                    message.domain === "holiday" ||
                                    message.domain === "navigation" ||
                                    message.data?.action_draft
                                ) {
                                    return null;
                                }

                                // Render metrics only when user query asks for balances, stats, breakdowns, or totals
                                const isDataQuery =
                                    msgText.includes("balance") ||
                                    msgText.includes("how many") ||
                                    msgText.includes("remaining") ||
                                    msgText.includes("taken") ||
                                    msgText.includes("total") ||
                                    msgText.includes("summary") ||
                                    msgText.includes("breakdown") ||
                                    msgText.includes("stat") ||
                                    msgText.includes("my leave") ||
                                    msgText.includes("ctc") ||
                                    msgText.includes("salary") ||
                                    message.data?.show_data_cards;

                                if (!isDataQuery) return null;

                                return <CopilotDataCards data={message.data} compact={compact} />;
                            })()}
                            {message.domain === "payroll" && message.data?.gross_earnings && (
                                <SalaryVisualizerCard data={message.data} compact={compact} />
                            )}
                            {((message.text && (
                                message.text.toLowerCase().includes("download") ||
                                message.text.toLowerCase().includes("get pdf") ||
                                message.text.toLowerCase().includes("export payslip")
                            )) || message.data?.show_download_card) && (
                                <DownloadPayslipCard payslipData={message.data} queryText={message.text} />
                            )}
                            {(() => {
                                // Do not render confirmation cards if this is a navigation query or missing module response
                                if (message.domain === "navigation" || message.intent === "navigation" || message.data?.navigation) {
                                    return null;
                                }

                                const data = message.data || {};

                                if (data.show_weekoff_confirmation || message.intent === "mark_weekoff") {
                                    return <WeekendWeekoffConfirmationCard queryText={message.text} />;
                                }

                                if (data.show_holiday_confirmation || message.intent === "mark_holiday") {
                                    return <CalendarHolidayConfirmationCard queryText={message.text} />;
                                }

                                return null;
                            })()}
                            {(message.domain !== "navigation" && message.intent !== "navigation" && !message.data?.navigation && message.text && (
                                message.text.toLowerCase().includes("request leave") ||
                                message.text.toLowerCase().includes("take leave") ||
                                message.text.toLowerCase().includes("book leave")
                            )) && !message.data?.action_draft && (
                                <ApplyLeaveShortcutCard />
                            )}
                            {((message.data?.pending_leave_approvals && message.data.pending_leave_approvals.length > 0) && (message.text && (
                                message.text.toLowerCase().includes("pending approval") ||
                                message.text.toLowerCase().includes("review leave") ||
                                message.text.toLowerCase().includes("approval inbox")
                            ))) && (
                                <ManagerDigestCard data={message.data} compact={compact} />
                            )}
                        </>
                    )}

                    {/* Feedback */}
                    {!isUser && !message.isError && onRate && (
                        <div className="mt-2.5 flex items-center gap-2 border-t border-border pt-2">
                            {!compact && (
                                <span className="text-[11px] text-muted-foreground">Helpful?</span>
                            )}
                            <button
                                type="button"
                                onClick={() => onRate(message.id, "up")}
                                title="Helpful"
                                className={cn(
                                    "rounded-md p-1 transition-colors",
                                    feedback === "up"
                                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                        : "text-muted-foreground hover:text-emerald-600"
                                )}
                            >
                                <ThumbsUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => onRate(message.id, "down")}
                                title="Not helpful"
                                className={cn(
                                    "rounded-md p-1 transition-colors",
                                    feedback === "down"
                                        ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                                        : "text-muted-foreground hover:text-destructive"
                                )}
                            >
                                <ThumbsDown className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <span
                className={cn(
                    "mt-1 text-[10px] text-muted-foreground",
                    compact ? "mx-9" : "mx-11"
                )}
            >
                {message.timestamp}
            </span>
        </div>
    );
};

export default CopilotMessage;
